import { useEffect, useMemo, useState } from "react";

const DISMISS_KEY = "migraine-tracker.pwa-install-popover-dismissed.v1";

const isIosSafari = (): boolean => {
  if (typeof navigator === "undefined") {
    return false;
  }
  const ua = navigator.userAgent.toLowerCase();
  const isIos = /iphone|ipad|ipod/.test(ua);
  const isWebKit = /safari/.test(ua) && !/crios|fxios|edgios/.test(ua);
  return isIos && isWebKit;
};

const isStandalone = (): boolean => {
  if (typeof window === "undefined") {
    return false;
  }
  const mediaStandalone = window.matchMedia("(display-mode: standalone)").matches;
  const navigatorStandalone = Boolean((window.navigator as Navigator & { standalone?: boolean }).standalone);
  return mediaStandalone || navigatorStandalone;
};

export function PwaInstallPopover() {
  const [dismissed, setDismissed] = useState<boolean>(() => {
    if (typeof window === "undefined") {
      return true;
    }
    return localStorage.getItem(DISMISS_KEY) === "1";
  });

  useEffect(() => {
    const onVisibility = () => {
      if (isStandalone()) {
        setDismissed(true);
      }
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, []);

  const shouldShow = useMemo(() => {
    return !dismissed && isIosSafari() && !isStandalone();
  }, [dismissed]);

  if (!shouldShow) {
    return null;
  }

  return (
    <div className="fixed bottom-20 left-1/2 z-30 w-[min(92vw,360px)] -translate-x-1/2 rounded-xl border border-[var(--color-border-medium)] bg-[var(--color-bg-elevated)]/95 p-3 text-sm text-[var(--color-text-primary)] shadow-lg backdrop-blur">
      <button
        type="button"
        aria-label="Dismiss install help"
        className="absolute right-2 top-2 rounded px-1 text-xs text-[var(--color-text-tertiary)] hover:text-[var(--color-text-primary)]"
        onClick={() => {
          localStorage.setItem(DISMISS_KEY, "1");
          setDismissed(true);
        }}
      >
        x
      </button>
      <p className="pr-4 text-[var(--color-text-primary)]">Install for quick access</p>
      <p className="mt-1 text-xs text-[var(--color-text-secondary)]">
        1. Tap the Share icon in Safari.
        <br />
        2. Tap <span className="font-medium text-[var(--color-text-primary)]">Add to Home Screen</span>.
      </p>
      <div className="pointer-events-none absolute -bottom-3 left-1/2 h-0 w-0 -translate-x-1/2 border-l-8 border-r-8 border-t-8 border-l-transparent border-r-transparent border-t-[var(--color-bg-elevated)]" />
    </div>
  );
}
